import { buildStorageKeyForNamespace, resolveCacheNamespace } from '@/shared/data/cacheNamespace';
import { buildCookieAuthToken } from '@/shared/data/client';

const STORAGE_KEY = 'flowqa-auth';
const SESSION_EXPIRED_KEY = 'flowqa-session-expired';
const MANUAL_SIGNOUT_KEY = 'flowqa-manual-signout';
const CLERK_PROVISIONING_KEY = 'flowqa-clerk-provisioning';
const E2E_ACCESS_TOKEN_KEY = 'flowqa-e2e-access-token';
const ORG_FILTER_KEY = 'flowqa-organisation-filter';
const PROJECT_FILTER_KEY = 'flowqa-project-filter';

export type StoredSession = {
  tokens: { accessToken: string };
  userId: string;
  orgId?: string | null;
  orgName?: string | null;
  expiresAt?: number;
  lastOrganisationId?: string | null;
  lastProjectId?: string | null;
  onboardingCompleted?: boolean;
  emailVerified?: boolean;
  roles?: string[];
};

type JwtPayload = {
  sub?: string;
  userId?: string;
  user_id?: string;
  orgId?: string;
  org_id?: string;
  org?: string;
  orgName?: string;
  org_name?: string;
  roles?: string[];
};

type LegacyStoredSession = StoredSession & {
  onboardingCompletedAt?: string | null;
  emailVerifiedAt?: string | null;
};

export const getE2eAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  const token = window.localStorage.getItem(E2E_ACCESS_TOKEN_KEY);
  if (!token) return null;
  return token;
};

export const clearE2eAccessToken = (): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(E2E_ACCESS_TOKEN_KEY);
};

const readFilterValue = (key: string, userId?: string | null): string | null => {
  if (typeof window === 'undefined') return null;
  const scopedKey = buildStorageKeyForNamespace(key, resolveCacheNamespace(userId));
  const value = window.localStorage.getItem(scopedKey) ?? window.localStorage.getItem(key);
  if (!value) return null;
  return value;
};

const writeFilterValue = (key: string, userId: string, value?: string | null): void => {
  if (typeof window === 'undefined') return;
  const scopedKey = buildStorageKeyForNamespace(key, resolveCacheNamespace(userId));
  if (value === undefined || value === null || value.length === 0) {
    window.localStorage.removeItem(scopedKey);
    return;
  }
  window.localStorage.setItem(scopedKey, value);
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1];
  if (!payload) return null;
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  try {
    const decoded = typeof atob === 'function' ? atob(padded) : '';
    if (!decoded) return null;
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
};

export const getStoredSession = (): StoredSession | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const e2eToken = getE2eAccessToken();
    if (!e2eToken) return null;
    const payload = decodeJwtPayload(e2eToken);
    const userId = payload?.sub ?? payload?.userId ?? payload?.user_id;
    if (!userId) return null;
    const orgId = payload?.orgId ?? payload?.org_id ?? payload?.org ?? null;
    const orgName = payload?.orgName ?? payload?.org_name ?? null;
    const lastOrganisationId = readFilterValue(ORG_FILTER_KEY, userId) ?? orgId;
    const lastProjectId = readFilterValue(PROJECT_FILTER_KEY, userId);
    return {
      userId,
      orgId,
      orgName,
      lastOrganisationId,
      lastProjectId,
      onboardingCompleted: false,
      emailVerified: false,
      tokens: { accessToken: e2eToken },
      roles: payload?.roles,
    };
  }
  try {
    const parsed = JSON.parse(raw) as LegacyStoredSession;
    const lastOrganisationId =
      parsed.lastOrganisationId !== undefined
        ? parsed.lastOrganisationId
        : (readFilterValue(ORG_FILTER_KEY, parsed.userId) ?? parsed.orgId ?? null);
    const lastProjectId =
      parsed.lastProjectId !== undefined
        ? parsed.lastProjectId
        : readFilterValue(PROJECT_FILTER_KEY, parsed.userId);
    const onboardingCompleted = parsed.onboardingCompleted ?? Boolean(parsed.onboardingCompletedAt);
    const emailVerified = parsed.emailVerified ?? Boolean(parsed.emailVerifiedAt);
    const accessToken = parsed.tokens.accessToken;
    if (!accessToken) {
      return {
        ...parsed,
        lastOrganisationId,
        lastProjectId,
        onboardingCompleted,
        emailVerified,
        tokens: { accessToken: buildCookieAuthToken(parsed.userId) },
      };
    }
    return { ...parsed, onboardingCompleted, emailVerified, lastOrganisationId, lastProjectId };
  } catch {
    return null;
  }
};

export const setStoredSession = (session: StoredSession | null): void => {
  if (typeof window === 'undefined') return;
  if (session) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
};

export const setSessionExpiredFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.sessionStorage.setItem(SESSION_EXPIRED_KEY, String(Date.now()));
  } else {
    window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  }
};

export const getSessionExpiredFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.sessionStorage.getItem(SESSION_EXPIRED_KEY));
};

export const clearSessionExpiredFlag = (): void => {
  setSessionExpiredFlag(false);
};

export const setManualSignOutFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.localStorage.setItem(MANUAL_SIGNOUT_KEY, String(Date.now()));
  } else {
    window.localStorage.removeItem(MANUAL_SIGNOUT_KEY);
  }
};

export const setClerkProvisioningFlag = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  if (value) {
    window.sessionStorage.setItem(CLERK_PROVISIONING_KEY, '1');
  } else {
    window.sessionStorage.removeItem(CLERK_PROVISIONING_KEY);
  }
};

export const getClerkProvisioningFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(CLERK_PROVISIONING_KEY) === '1';
};

export const getManualSignOutFlag = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.localStorage.getItem(MANUAL_SIGNOUT_KEY));
};

export const updateSessionContext = (
  lastOrganisationId?: string | null,
  lastProjectId?: string | null,
): void => {
  const current = getStoredSession();
  if (!current) return;
  const next: StoredSession = { ...current };
  if (lastOrganisationId !== undefined) {
    next.lastOrganisationId = lastOrganisationId;
  }
  if (lastProjectId !== undefined) {
    next.lastProjectId = lastProjectId;
  }
  setStoredSession(next);
  if (lastOrganisationId !== undefined) {
    writeFilterValue(ORG_FILTER_KEY, current.userId, lastOrganisationId);
  }
  if (lastProjectId !== undefined) {
    writeFilterValue(PROJECT_FILTER_KEY, current.userId, lastProjectId);
  }
};

export const getStoredContext = (): {
  lastOrganisationId: string | null;
  lastProjectId: string | null;
} => {
  const current = getStoredSession();
  return {
    lastOrganisationId: current?.lastOrganisationId ?? null,
    lastProjectId: current?.lastProjectId ?? null,
  };
};

export { STORAGE_KEY };
